"use client"

import { useCallback } from "react"
import usePromotionEventStore, {
  EventActionType,
} from "../store/usePromotionEventStore"
import {
  useGlobalPopupStore,
  type PopupOpenOptions,
} from "@/components/common/popup/store/useGlobalPopupStore"
import { PROMOTION_EVENT_POPUP_CONFIG } from "../EventConfig"

/**
 * 프로모션 이벤트를 처리하는 커스텀 훅
 * 액션을 입력받아 해당 액션을 포함하는 모든 활성 이벤트를 자동으로 처리합니다.
 *
 * @example
 * const { checkEvent } = usePromotionEvent()
 * checkEvent('book')     // 책 등록 완료 시 - book 액션을 가진 모든 이벤트 처리
 * checkEvent('sentence') // 문장 등록 완료 시 - sentence 액션을 가진 모든 이벤트 처리
 */
const usePromotionEvent = () => {
  const {
    rlEvents,
    getEventsWithAction,
    removeEvent,
    markEventAction,
    clearExpiredEvents,
  } = usePromotionEventStore()
  const { openPopup, closePopup } = useGlobalPopupStore()

  const showPopup = useCallback(
    (eventCode: string, options?: PopupOpenOptions) => {
      const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
      if (!config) return

      const PopupComponent = config.component
      openPopup(<PopupComponent onClose={() => closePopup()} />, options)
    },
    [openPopup, closePopup],
  )

  /**
   * 액션 기반 이벤트 처리
   * 해당 액션을 포함하는 모든 활성 이벤트를 찾아 처리합니다.
   * - 모든 액션이 완료된 이벤트: 팝업 표시 후 제거
   * - 아직 미완료 액션이 있는 이벤트: 해당 액션만 완료 표시
   *
   * @param action - 완료된 액션 타입 (예: "book", "sentence")
   * @param options - 팝업 옵션 (예: { mode: "queue" })
   */
  const checkEvent = useCallback(
    (action: EventActionType, options?: PopupOpenOptions) => {
      clearExpiredEvents()

      // 해당 액션을 포함하는 모든 활성 이벤트 조회
      const eventsWithAction = getEventsWithAction(action)
      if (eventsWithAction.length === 0) return

      // 각 이벤트에 대해 액션 처리
      for (const eventCode of eventsWithAction) {
        // 액션 완료 표시
        markEventAction(eventCode, action)

        // 모든 액션이 완료되었는지 확인
        const complete = usePromotionEventStore
          .getState()
          .isEventComplete(eventCode)

        if (complete) {
          showPopup(eventCode, options)
          removeEvent(eventCode)
        }
      }
    },
    [
      getEventsWithAction,
      markEventAction,
      showPopup,
      clearExpiredEvents,
      removeEvent,
    ],
  )

  return {
    checkEvent,
    currentEvents: rlEvents,
    hasAnyEvent: rlEvents.length > 0,
  }
}

export default usePromotionEvent
